import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ScrollView,
  KeyboardAvoidingView,
  ActivityIndicator,
  Image,
} from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import * as Linking from 'expo-linking';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useTheme } from '@/lib/styling';
import type { Theme } from '@/theme';
import { Box, Text, PressableBox } from '@/components/ui/primitives';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Eye, EyeOff } from 'lucide-react-native';
import { Icon } from '@/components/ui/icon';
import { useAuth, type AuthenticatedUser } from '@/hooks/useAuth';
import { apiRequestJson, queryClient } from '@/lib/queryClient';
import { config } from '@/config/env';
import { betterAuthClient } from '@/lib/betterAuthClient';
import { isIOS, isWeb } from '@/lib/platform';
import { clientLogger } from '@/lib/datadog';
import { getUserFacingErrorMessage } from '@/lib/networkErrors';
import type { RootStackParamList } from '@/types/navigation';
import {
  clearRedirectAfterLogin,
  getPendingInviteCode,
  getRedirectAfterLogin,
  setPendingInviteCode,
  setRedirectAfterLogin,
} from '@/lib/storage';
import {
  getAuthHref,
  getForgotPasswordHref,
  getJoinLeagueHref,
  getLandingHref,
  normalizeBooleanParam,
  normalizeStringParam,
  resolveAuthRedirect,
  resolvePostLoginHref,
} from '@/lib/routes';
import { colors, typography } from '@/lib/theme';

const PasswordInput = ({
  value,
  onChangeText,
  placeholder,
  editable,
  testID,
}: {
  value: string;
  onChangeText: (text: string) => void;
  placeholder: string;
  editable: boolean;
  testID?: string;
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const theme = useTheme<Theme>();

  return (
    <Box position="relative" justifyContent="center">
      <Input
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        secureTextEntry={!showPassword}
        autoComplete="password"
        editable={editable}
        testID={testID}
        style={passwordInputStyle}
      />
      <PressableBox
        position="absolute"
        right={theme.spacing.m}
        height="100%"
        justifyContent="center"
        onPress={() => setShowPassword(!showPassword)}
        accessibilityRole="button"
        accessibilityLabel={showPassword ? 'Hide password' : 'Show password'}
        disabled={!editable}
      >
        <Icon
          as={showPassword ? EyeOff : Eye}
          size={20}
          color={theme.colors.mutedForeground}
        />
      </PressableBox>
    </Box>
  );
};

const modes = {
  signIn: 'Sign In',
  signUp: 'Create Account',
} as const;

type Mode = keyof typeof modes;

const passwordInputStyle = { paddingRight: 40 };
const authButtonStyle = { height: 56 };
const googleLogoStyle = { width: 20, height: 20 };
const authModeTextStyle = {
  color: colors.ink.press,
  fontFamily: typography.fontFamily.mono,
  textTransform: 'uppercase' as const,
};
const getAuthModeButtonStyle = (isActive: boolean, theme: Theme) => ({
  backgroundColor: isActive ? theme.colors.acid : theme.colors.background,
  borderColor: theme.colors.press,
  shadowColor: theme.colors.press,
  shadowOffset: isActive ? { width: 4, height: 4 } : { width: 2, height: 2 },
  shadowOpacity: 1,
  shadowRadius: 0,
  elevation: isActive ? 3 : 1,
});

export default function AuthScreen() {
  const router = useRouter();
  const routeParams = useLocalSearchParams<{
    inviteCode?: string | string[];
    redirectTo?: string | string[];
    autoJoin?: string | string[];
    startGoogleSignIn?: string | string[];
  }>();
  const theme = useTheme<Theme>();
  const { user, isAuthenticated, isLoading } = useAuth();
  const hasWebRedirectedRef = useRef(false);
  const hasAutoGoogleLaunchRef = useRef(false);
  const emailPasswordEnabled = config.auth.emailPasswordEnabled;
  const [mode, setMode] = useState<Mode>('signIn');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoogleSubmitting, setIsGoogleSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [signInEmail, setSignInEmail] = useState('');
  const [signInPassword, setSignInPassword] = useState('');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [signUpEmail, setSignUpEmail] = useState('');
  const [signUpPassword, setSignUpPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const isSignIn = mode === 'signIn';
  const canUseGoogleSignIn = config.socialAuth.googleEnabled;
  const isBusy = isSubmitting || isGoogleSubmitting;

  const headerDescription = useMemo(() => {
    if (!emailPasswordEnabled) {
      return 'Use Google to quickly sign in and start making your picks.';
    }
    return isSignIn
      ? 'Welcome back! Sign in to manage your picks, leagues, and standings.'
      : 'Create a PCKM account to join leagues, make weekly picks, and track performance.';
  }, [emailPasswordEnabled, isSignIn]);

  const fetchAndCacheAuthUser = useCallback(async () => {
    const authPayload = await apiRequestJson<AuthenticatedUser>('/api/auth/user', 'GET');
    queryClient.setQueryData(['/api/auth/user'], authPayload);
    return authPayload;
  }, []);

  const handleSignIn = async () => {
    const email = signInEmail.trim();
    if (!email || !signInPassword) {
      setErrorMessage('Email and password are required to sign in.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const result = await betterAuthClient.signIn.email({
        email,
        password: signInPassword,
        rememberMe: true,
      });

      if (result.error) {
        throw new Error(result.error.message || 'Sign in failed');
      }

      const authPayload = await fetchAndCacheAuthUser();

      await handlePostAuthNavigation(authPayload);
    } catch (error) {
      clientLogger.error('Sign-in failed', { error });
      setErrorMessage(getUserFacingErrorMessage(error, 'Sign in failed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignUp = async () => {
    const email = signUpEmail.trim();
    if (!firstName.trim() || !lastName.trim()) {
      setErrorMessage('First and last name are required to create an account.');
      return;
    }
    if (!email || !signUpPassword) {
      setErrorMessage('Email and password are required to create an account.');
      return;
    }
    if (signUpPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const signUpResult = await betterAuthClient.signUp.email({
        email,
        password: signUpPassword,
        name: `${firstName.trim()} ${lastName.trim()}`.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });

      if (signUpResult.error) {
        throw new Error(signUpResult.error.message || 'Account creation failed');
      }

      setSuccessMessage('Account created! Signing you in...');

      const signInResult = await betterAuthClient.signIn.email({
        email,
        password: signUpPassword,
        rememberMe: true,
      });

      if (signInResult.error) {
        throw new Error(signInResult.error.message || 'Sign in after account creation failed');
      }

      const authPayload = await fetchAndCacheAuthUser();

      await handlePostAuthNavigation(authPayload);
    } catch (error) {
      clientLogger.error('Account creation failed', { error });
      setErrorMessage(getUserFacingErrorMessage(error, 'Account creation failed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const routeInviteCode = normalizeStringParam(routeParams.inviteCode)?.toUpperCase();
  const routeRedirectTo = normalizeStringParam(routeParams.redirectTo);
  const routeAutoJoin = normalizeBooleanParam(routeParams.autoJoin, false);
  const routeStartGoogleSignIn = normalizeBooleanParam(routeParams.startGoogleSignIn, false);

  useEffect(() => {
    const syncRedirectState = async () => {
      if (routeInviteCode) {
        await setPendingInviteCode(routeInviteCode.toUpperCase());
      }

      if (routeRedirectTo) {
        await setRedirectAfterLogin({
          routeName: routeRedirectTo as keyof RootStackParamList,
          params: routeInviteCode
            ? { inviteCode: routeInviteCode, autoJoin: routeAutoJoin ?? true }
            : undefined,
        });
      }
    };

    void syncRedirectState();
  }, [routeAutoJoin, routeInviteCode, routeRedirectTo]);

  const handlePostAuthNavigation = useCallback(
    async (authUserOverride?: AuthenticatedUser | null) => {
      const redirect = await getRedirectAfterLogin();
      const pendingInviteCode = await getPendingInviteCode();

      const redirectHref = resolveAuthRedirect(redirect, pendingInviteCode ?? routeInviteCode ?? null);
      if (redirectHref) {
        await clearRedirectAfterLogin();
        router.replace(redirectHref);
        return;
      }

      const inviteCode = routeInviteCode || pendingInviteCode;
      if (routeRedirectTo === 'JoinLeague' && inviteCode) {
        router.replace(getJoinLeagueHref(inviteCode, true));
        return;
      }

      const destinationUser = authUserOverride ?? user;
      router.replace(resolvePostLoginHref(destinationUser));
    },
    [routeInviteCode, routeRedirectTo, router, user],
  );

  const handleSubmit = () => {
    if (!emailPasswordEnabled) {
      return;
    }
    if (isSignIn) {
      void handleSignIn();
    } else {
      void handleSignUp();
    }
  };

  // Web-only: after OAuth callback reloads to /auth with a session, send user home once.
  useEffect(() => {
    if (!isWeb) return;
    if (isLoading) return;
    if (!isAuthenticated) return;
    if (hasWebRedirectedRef.current) return;
    hasWebRedirectedRef.current = true;
    void handlePostAuthNavigation(user);
  }, [handlePostAuthNavigation, isAuthenticated, isLoading, user]);

  const handleGoogleSignIn = useCallback(async () => {
    if (!canUseGoogleSignIn) {
      setErrorMessage('Google sign-in is not available right now.');
      return;
    }

    const callbackPath = String(getAuthHref());
    const callbackURL = isWeb
      ? callbackPath
      : Linking.createURL(callbackPath, { scheme: 'pckm' });

    setIsGoogleSubmitting(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const result = await betterAuthClient.signIn.social({
        provider: 'google',
        callbackURL,
        errorCallbackURL: callbackURL,
        newUserCallbackURL: callbackURL,
      });

      if (result.error) {
        throw new Error(result.error.message || 'Google sign-in failed');
      }

      const authPayload = await fetchAndCacheAuthUser();
      await handlePostAuthNavigation(authPayload);
    } catch (error) {
      clientLogger.error('Google sign-in failed', { error });
      setErrorMessage(getUserFacingErrorMessage(error, 'Google sign-in failed'));
    } finally {
      setIsGoogleSubmitting(false);
    }
  }, [canUseGoogleSignIn, fetchAndCacheAuthUser, handlePostAuthNavigation]);

  useEffect(() => {
    if (!routeStartGoogleSignIn) return;
    if (!canUseGoogleSignIn) return;
    if (hasAutoGoogleLaunchRef.current) return;
    hasAutoGoogleLaunchRef.current = true;
    void handleGoogleSignIn();
  }, [canUseGoogleSignIn, handleGoogleSignIn, routeStartGoogleSignIn]);

  return (
    <Box style={styles.screen}>
      <Box style={styles.gridLayer} />
      <ScrollView
        contentContainerStyle={styles.content}
        keyboardShouldPersistTaps="handled"
      >
        <KeyboardAvoidingView
          behavior={isIOS ? 'padding' : undefined}
          style={styles.panel}
        >
          <Box style={styles.header}>
            <Text variant="caption" style={styles.kicker}>
              ACCOUNT ACCESS / SECURE PICK SHEET
            </Text>
            <Box style={styles.titleWrap}>
              <Box style={styles.acidSlab} />
              <Text variant="h2" style={styles.title}>
              {modes[mode]}
            </Text>
            </Box>
            <Text variant="body" color="pressMuted" style={styles.headerDescription}>
              {headerDescription}
            </Text>
          </Box>

          {emailPasswordEnabled ? (
            <>
              <Box flexDirection="row" gap="m" justifyContent="center">
                {(Object.keys(modes) as Mode[]).map((key) => (
                  <PressableBox
                    key={key}
                    flex={1}
                    borderRadius="none"
                    borderWidth={3}
                    paddingVertical="m"
                    style={getAuthModeButtonStyle(mode === key, theme)}
                    onPress={() => {
                      setMode(key);
                      setErrorMessage(null);
                      setSuccessMessage(null);
                    }}
                    accessibilityRole="button"
                    accessibilityState={{ selected: mode === key }}
                  >
                    <Text
                      textAlign="center"
                      fontWeight="600"
                      style={authModeTextStyle}
                    >
                      {modes[key]}
                    </Text>
                  </PressableBox>
                ))}
              </Box>

              <Box gap="m">
                {isSignIn ? (
                  <>
                    <Input
                      value={signInEmail}
                      onChangeText={setSignInEmail}
                      placeholder="Email"
                      autoCapitalize="none"
                      keyboardType="email-address"
                      autoComplete="email"
                      editable={!isBusy}
                      testID="auth-email-input"
                    />
                    <PasswordInput
                      value={signInPassword}
                      onChangeText={setSignInPassword}
                      placeholder="Password"
                      editable={!isBusy}
                      testID="auth-password-input"
                    />
                    <PressableBox
                      onPress={() => router.push(getForgotPasswordHref())}
                      alignSelf="flex-start"
                      paddingVertical="xs"
                      disabled={isBusy}
                    >
                      <Text variant="button" color="press" textDecorationLine="underline">
                        Forgot password?
                      </Text>
                    </PressableBox>
                  </>
                ) : (
                  <>
                    <Box flexDirection="row" gap="m">
                      <Box flex={1}>
                        <Input
                          value={firstName}
                          onChangeText={setFirstName}
                          placeholder="First Name"
                          autoCapitalize="words"
                          editable={!isBusy}
                        />
                      </Box>
                      <Box flex={1}>
                        <Input
                          value={lastName}
                          onChangeText={setLastName}
                          placeholder="Last Name"
                          autoCapitalize="words"
                          editable={!isBusy}
                        />
                      </Box>
                    </Box>
                    <Input
                      value={signUpEmail}
                      onChangeText={setSignUpEmail}
                      placeholder="Email"
                      autoCapitalize="none"
                      keyboardType="email-address"
                      autoComplete="email"
                      editable={!isBusy}
                    />
                    <PasswordInput
                      value={signUpPassword}
                      onChangeText={setSignUpPassword}
                      placeholder="Password"
                      editable={!isBusy}
                    />
                    <PasswordInput
                      value={confirmPassword}
                      onChangeText={setConfirmPassword}
                      placeholder="Confirm Password"
                      editable={!isBusy}
                    />
                  </>
                )}
              </Box>
            </>
          ) : (
            <Box
              borderRadius="none"
              borderWidth={3}
              borderColor="press"
              padding="m"
              backgroundColor="paperDark"
            >
              <Text textAlign="center" color="text">
                Email and password login is disabled on this environment. Continue with Google below.
              </Text>
            </Box>
          )}

          {errorMessage ? (
            <Text textAlign="center" color="destructive">
              {errorMessage}
            </Text>
          ) : null}
          {successMessage ? (
            <Text textAlign="center" color="success">
              {successMessage}
            </Text>
          ) : null}

          {emailPasswordEnabled && (
            <Button
              onPress={handleSubmit}
              disabled={isBusy}
              marginTop="m"
              size="lg"
              testID={isSignIn ? 'auth-sign-in-button' : 'auth-sign-up-button'}
              accessibilityLabel={modes[mode]}
              style={authButtonStyle} // maintaining h-14
            >
              {isSubmitting ? (
                <ActivityIndicator color={theme.colors.press} />
              ) : (
                <Text variant="button" fontSize={18} fontWeight="700" color="primaryForeground">
                  {modes[mode]}
                </Text>
              )}
            </Button>
          )}

          {canUseGoogleSignIn && (
            <>
              <Box marginTop="s" flexDirection="row" alignItems="center" gap="m">
                <Box flex={1} height={1} backgroundColor="border" />
                <Text variant="caption" textTransform="uppercase" letterSpacing={1} color="mutedForeground">
                  or continue with
                </Text>
                <Box flex={1} height={1} backgroundColor="border" />
              </Box>
              <Button
                variant="outline"
                marginTop="s"
                disabled={isBusy}
                onPress={() => handleGoogleSignIn()}
                accessibilityLabel="Sign in with Google"
                backgroundColor={theme.colors.background}
              >
                {isGoogleSubmitting ? (
                  <ActivityIndicator color={theme.colors.mutedForeground} />
                ) : (
                <Box flexDirection="row" alignItems="center" justifyContent="center" gap="s">
                    <Image
                      source={require('../../assets/google-g.png')}
                      style={googleLogoStyle}
                      resizeMode="contain"
                    />
                    <Text fontWeight="600" fontSize={16} color="text">
                      Sign in with Google
                    </Text>
                  </Box>
                )}
              </Button>
            </>
          )}

          <PressableBox
            onPress={() => router.replace(getLandingHref())}
            alignSelf="center"
            padding="s"
          >
            <Text variant="button" color="press">
              Back to overview
            </Text>
          </PressableBox>
        </KeyboardAvoidingView>
      </ScrollView>
    </Box>
  );
}

const styles = StyleSheet.create((theme, runtime) => {
  const isWide = runtime.screen.width >= theme.breakpoints.sm;

  return {
    acidSlab: {
      backgroundColor: theme.colors.acid,
      bottom: -4,
      height: 6,
      left: 0,
      position: 'absolute',
      right: 8,
    },
    content: {
      alignItems: 'center',
      flexGrow: 1,
      justifyContent: 'center',
      paddingBottom: 48,
      paddingHorizontal: isWide ? 32 : 16,
      paddingTop: isWide ? 76 : 36,
    },
    gridLayer: {
      borderColor: theme.colors.press,
      borderWidth: 1,
      bottom: 0,
      left: 0,
      opacity: 0.1,
      position: 'absolute',
      right: 0,
      top: 0,
      transform: [{ rotate: '1deg' }],
    },
    header: {
      gap: 12,
    },
    headerDescription: {
      maxWidth: 460,
    },
    kicker: {
      color: theme.colors.pressMuted,
    },
    panel: {
      backgroundColor: theme.colors.background,
      borderColor: theme.colors.press,
      borderWidth: 5,
      elevation: 8,
      gap: theme.spacing.l,
      maxWidth: 560,
      padding: isWide ? 28 : 18,
      shadowColor: theme.colors.press,
      shadowOffset: { width: 14, height: 14 },
      shadowOpacity: 1,
      shadowRadius: 0,
      width: '100%',
    },
    screen: {
      backgroundColor: theme.colors.background,
      flex: 1,
    },
    title: {
      color: theme.colors.press,
    },
    titleWrap: {
      alignSelf: 'flex-start',
      position: 'relative',
    },
  };
});
